// lib/koi-pond-v3/shaders/waterSurfaceV3.ts
//
// Water surface plane viewed from above. Two octaves of value noise
// scrolling at different speeds + directions compute the surface
// normal in real-time. T7.5 layers the moon reflection on top:
// for each fragment, reflect the view direction across the surface
// normal — if the reflected ray points toward the moon, the
// fragment glows with moonlight color. Ripples passing through the
// reflection break it into shimmering fragments.
//
// Surface is semi-transparent so koi swimming below (Phase 8) are
// visible. depthWrite:true (load-bearing — same lesson as v2 Phase
// 5 T5.3d: depthWrite:false would let underwater geometry bleed
// through cleanly, breaking the depth illusion).

import { ShaderMaterial, Vector2, Vector3, Color } from 'three';
import { paletteVec3 } from '../palette';
import { DEPTH_FOG_UNIFORM_DECL, DEPTH_FOG_APPLY_SNIPPET } from './depthFog';

export const WATER_SURFACE_V3_VERT = `
  varying vec2 vUv;
  varying vec3 vWorldPos;
  void main() {
    vUv = uv;
    vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const WATER_SURFACE_V3_FRAG = `
  precision highp float;
  varying vec2 vUv;
  varying vec3 vWorldPos;
  uniform float uTime;
  uniform vec3 uMoonWorldPos;
  uniform vec3 uCameraPos;
  uniform float uMoonDim; // T10.3 cloud-dimming (1 = full, 0.7 = dimmed)
  // T11.2 — local surface disturbances (clicks, cursor wake, surfacing
  // koi). Each is an expanding ring of ripples that perturbs the surface
  // normal locally, so the moon reflection breaks where it lands.
  uniform int uDisturbCount;
  uniform vec2 uDisturbPos[8];
  uniform float uDisturbAge[8];
  uniform float uDisturbStr[8];
  // T11.4 — cursor lens: a gentle persistent tilt toward the cursor bends
  // the reflection where it overlaps the cursor.
  uniform vec2 uCursorPos;
  uniform float uCursorAmt;
  // Dragon Gate — pond-wide shockwave (rest uShockStr=0 = no-op).
  uniform vec2 uShockOrigin;
  uniform float uShockAge;
  uniform float uShockStr;
  ${DEPTH_FOG_UNIFORM_DECL}

  // Two-octave value noise for surface normal computation.
  vec2 hash2(vec2 p) {
    p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
    return fract(sin(p) * 43758.5453) * 2.0 - 1.0;
  }
  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = dot(hash2(i),               f - vec2(0.0, 0.0));
    float b = dot(hash2(i + vec2(1, 0)),  f - vec2(1.0, 0.0));
    float c = dot(hash2(i + vec2(0, 1)),  f - vec2(0.0, 1.0));
    float d = dot(hash2(i + vec2(1, 1)),  f - vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  // Surface height at (uv). Octave 1: low-frequency swell, slow drift.
  // Octave 2: high-frequency capillary ripples, faster cross-drift.
  // Both contribute to surface displacement; their derivatives form
  // the surface normal.
  float surfaceHeight(vec2 uv) {
    float o1 = noise(uv * 2.5 + vec2( uTime * 0.06,  uTime * 0.03)) * 0.5;
    float o2 = noise(uv * 9.0 + vec2(-uTime * 0.12, uTime * 0.10)) * 0.18;
    return o1 + o2;
  }

  // Surface normal via central differences on surfaceHeight.
  // Y component is the "up" direction (water surface is horizontal).
  vec3 surfaceNormal(vec2 uv) {
    float eps = 0.01;
    float hL = surfaceHeight(uv - vec2(eps, 0.0));
    float hR = surfaceHeight(uv + vec2(eps, 0.0));
    float hD = surfaceHeight(uv - vec2(0.0, eps));
    float hU = surfaceHeight(uv + vec2(0.0, eps));
    return normalize(vec3(hL - hR, 2.0 * eps, hD - hU));
  }

  void main() {
    // Sample normal at higher UV frequency than the geometry — gives
    // a denser ripple field than the 64x48 vertex grid alone.
    vec3 n = surfaceNormal(vUv * 6.0);

    // T11.2 — disturbance ripples: each expanding ring tilts the normal
    // radially near its wavefront, so the moon reflection shatters locally
    // where a click / surfacing koi / cursor wake lands (analytic radial
    // slope — no per-fragment sampling loop).
    vec2 fragXZd = vec2(vWorldPos.x, vWorldPos.z);
    vec2 distGrad = vec2(0.0);
    for (int i = 0; i < 8; i++) {
      if (i >= uDisturbCount) break;
      vec2 rel = fragXZd - uDisturbPos[i];
      float dist = length(rel);
      float age = uDisturbAge[i];
      float ringR = age * 2.5;                          // ring expands at 2.5 u/s
      float life = clamp(1.0 - age / 2.5, 0.0, 1.0);    // fade over ~2.5s
      float band = exp(-(dist - ringR) * (dist - ringR) * 1.2);
      float phase = (dist - ringR) * 7.0;
      float slope = cos(phase) * 7.0 * 0.5 * uDisturbStr[i] * life * band;
      if (dist > 1e-3) distGrad += (rel / dist) * slope;
    }
    n = normalize(n - vec3(distGrad.x, 0.0, distGrad.y));

    // T11.4 — cursor lens. A gentle, persistent radial tilt toward the
    // cursor (no wave, no expansion — distinct from the disturbance
    // ripples and from T11.5's sharp break). Invisible on dark water; only
    // shows where it overlaps the moon reflection, bending it toward the
    // cursor. uCursorAmt eases in/out so it appears + settles smoothly.
    if (uCursorAmt > 0.001) {
      vec2 cRel = fragXZd - uCursorPos;
      float g = uCursorAmt * exp(-dot(cRel, cRel) * 0.18);
      n = normalize(n - vec3(cRel.x, 0.0, cRel.y) * g * 0.22);
    }

    // Dragon Gate — pond-wide shockwave. A single expanding bright wavefront
    // (rest uShockStr=0 → skipped) tilts the normal radially so the moon
    // reflection ripples through it; the crest is added to the color below.
    float shockCrest = 0.0;
    if (uShockStr > 0.001) {
      float ds = distance(fragXZd, uShockOrigin);
      float radius = uShockAge * 14.0;          // sweeps the 20x14 pond
      float bandS = exp(-pow((ds - radius) * 1.0, 2.0)); // tighter = more defined wavefront
      shockCrest = bandS * uShockStr;
      vec2 sdir = normalize(fragXZd - uShockOrigin + 1e-4);
      n = normalize(n - vec3(sdir.x, 0.0, sdir.y) * shockCrest * 1.4);
    }

    // Base water tint visible through surface. Slight darkening at
    // grazing angles via fresnel-style mix gives the surface depth.
    vec3 viewDir = normalize(uCameraPos - vWorldPos);
    float NdotV = max(0.0, dot(vec3(0.0, 1.0, 0.0), viewDir));
    vec3 deepTint = ${paletteVec3('deepWater')};
    vec3 nearTint = ${paletteVec3('midWater')};
    vec3 tint = mix(deepTint, nearTint, NdotV);

    // T7.5 — moon reflection. Reflect view direction across surface
    // normal: that's the direction the eye sees IF the surface acted
    // as a mirror at this fragment. If that reflected direction
    // points toward the moon, the fragment glows with moonlight.
    vec3 reflectDir = reflect(-viewDir, n);
    vec3 toMoon = normalize(uMoonWorldPos - vWorldPos);
    float moonAlign = max(0.0, dot(reflectDir, toMoon));

    // T7.6g — cone widened from (0.985, 0.999) to (0.92, 0.999).
    // Real moonlit water shows a long shimmering LANE extending toward
    // the viewer because surface ripples scatter the moon's light
    // across a wide angular range. The 0.92 floor means any surface
    // fragment within ~23° of perfect mirror alignment contributes to
    // the lane — broader patch, faster ripple variation.
    float scatter = smoothstep(0.92, 0.999, moonAlign);

    // T7.6i — moon trail spine + grazing falloff.
    // T7.6j round 4 recalibration: round 3 over-dimmed broad scatter
    // by multiplying with grazing × (0.40 + 0.60 * spine). Owner
    // feedback was "ADD spine to broad scatter," not "replace broad
    // scatter with spine." Round 4 keeps the round-2 scatter brightness
    // as baseline + adds spine as an ADDITIVE boost on top + uses
    // only a subtle grazing dim (0.55 floor, not full darkening).
    //
    // Spine geometry: the line on the water surface (y=1 plane)
    // connecting the moon's xz projection and the viewer's xz projection.
    // Each water fragment's perpendicular distance from this line drives
    // the spine intensity — bright near the line, fading laterally.
    vec2 moonXZ = vec2(uMoonWorldPos.x, uMoonWorldPos.z);
    vec2 viewerXZ = vec2(uCameraPos.x, uCameraPos.z);
    vec2 fragXZ = vec2(vWorldPos.x, vWorldPos.z);
    vec2 spineDir = normalize(viewerXZ - moonXZ);
    vec2 offsetFromMoon = fragXZ - moonXZ;
    float alongSpine = dot(offsetFromMoon, spineDir);
    vec2 perpVec = offsetFromMoon - alongSpine * spineDir;
    float perpDist = length(perpVec);
    // Gaussian falloff laterally. The 0.30 multiplier controls spine
    // width — lower = wider spine, higher = tighter spine. Tuned so
    // the spine reads as a path ~3-4 world units wide on the visible
    // water, not a sharp ridge.
    float spineIntensity = exp(-perpDist * perpDist * 0.30);

    // Subtle grazing dim. Real moon trails are slightly brighter at
    // the horizon end (grazing angle catches more light) and slightly
    // dimmer in the near foreground. 0.55 floor → 1.0 ceiling means
    // near-foreground water keeps 55% of brightness, not full dark.
    float horizDist = length(fragXZ - viewerXZ);
    float grazingFactor = mix(0.55, 1.0, smoothstep(0.0, 6.0, horizDist));

    // Final composite: full scatter brightness baseline, spine adds
    // 80% boost on top where it lands, subtle grazing dim. Result:
    // round 2's broad shimmer preserved + luminous spine concentrated
    // along moon-viewer azimuth.
    float reflectionIntensity = scatter * grazingFactor * (1.0 + 0.80 * spineIntensity) * uMoonDim;

    vec3 moonReflection = ${paletteVec3('moonlight')} * reflectionIntensity;

    // Composite: surface tint + moon reflection (additive)
    vec3 color = tint + moonReflection;
    // Dragon-gate shock crest — bright expanding ring (rest shockCrest=0).
    color += ${paletteVec3('moonlight')} * shockCrest * 2.4;

    // Apply depth fog so far edges of the water plane fade with the rest
    // of the scene
    ${DEPTH_FOG_APPLY_SNIPPET}

    // T8.1b — alpha lowered 0.85 → 0.55 (baseline). 0.85 hid the
    // koi underneath; 0.55 gives ~45% transmission for what's below
    // so the koi swimming under the surface are clearly visible
    // while the moon reflection trail stays bright (alpha boost up
    // to 0.40 where reflection lands keeps the trail crisp).
    float alpha = 0.55 + reflectionIntensity * 0.40 + shockCrest * 0.4;

    gl_FragColor = vec4(color, alpha);
  }
`;

export function makeWaterSurfaceV3Material(
  sharedFogUniforms: { uFogColor: { value: Color }; uFogDensity: { value: number } },
): {
  material: ShaderMaterial;
  uniforms: {
    uTime: { value: number };
    uMoonWorldPos: { value: Vector3 };
    uCameraPos: { value: Vector3 };
    uMoonDim: { value: number };
    uDisturbCount: { value: number };
    uDisturbPos: { value: Vector2[] };
    uDisturbAge: { value: number[] };
    uDisturbStr: { value: number[] };
    uCursorPos: { value: Vector2 };
    uCursorAmt: { value: number };
    uShockOrigin: { value: Vector2 };
    uShockAge: { value: number };
    uShockStr: { value: number };
    uResolution: { value: Vector2 };
    uFogColor: { value: Color };
    uFogDensity: { value: number };
  };
} {
  const uniforms = {
    uTime: { value: 0 },
    uMoonWorldPos: { value: new Vector3() },
    uCameraPos: { value: new Vector3() },
    uMoonDim: { value: 1 },
    uDisturbCount: { value: 0 },
    uDisturbPos: { value: Array.from({ length: 8 }, () => new Vector2()) },
    uDisturbAge: { value: new Array(8).fill(0) },
    uDisturbStr: { value: new Array(8).fill(0) },
    uCursorPos: { value: new Vector2(999, 999) },
    uCursorAmt: { value: 0 },
    uShockOrigin: { value: new Vector2(0, 0) },
    uShockAge: { value: 0 },
    uShockStr: { value: 0 },
    uResolution: { value: new Vector2() },
    uFogColor: sharedFogUniforms.uFogColor,
    uFogDensity: sharedFogUniforms.uFogDensity,
  };
  const material = new ShaderMaterial({
    vertexShader: WATER_SURFACE_V3_VERT,
    fragmentShader: WATER_SURFACE_V3_FRAG,
    uniforms,
    transparent: true,
    depthWrite: true, // load-bearing — same lesson as v2 Phase 5 T5.3d
  });
  return { material, uniforms };
}
